import { Grid, IconButton, Typography, makeStyles } from "@material-ui/core";
import CloseIcon from "@material-ui/icons/Close";
import { ICityItemToBilling } from "api/interfaces";
import AutoCompleteField from "components/UI/Fields/AutoCompleteField";
import MaskedField from "components/UI/Fields/MaskedField";
import { lang } from "i18nLang";
import { useEffect, useState } from "react";
import { ContractIntegrationStore, SendToBillingStore as Store } from "store";
import { formatNumberToMonetary } from "utils";
import { decimal } from "utils/DecimalPrecision";

const useStyles = makeStyles((theme) => ({
  text: {
    paddingTop: "1.4rem",
  },
  labelValue: {
    color: theme.palette.gray?.[600],
    paddingTop: "1.4rem",
  },
  line: {
    "& .actionButton": {
      marginLeft: -12,
      padding: 6,
      opacity: 0,
      transition: "opacity 200ms ease-in",
    },
    "&:hover .actionButton": { opacity: 1 },
  },
  input: { width: 180 },
}));

/** Linha da tabela de 'Recursos por Municípios' do envio para Faturamento. */
const ResourcesByCityRow = ({
  index,
  rowData,
  apportiongTotal,
  separateReadjust,
}: {
  /** Index da linha. */
  index: number;
  /** Dados da linha. */
  rowData: ICityItemToBilling;
  /** Total de rateio já aplicado nos itens da tabela. */
  apportiongTotal: number;
  /** Sinaliza que o valor de reajuste deve ser separado. */
  separateReadjust: boolean;
}) => {
  const classes = useStyles();
  const [data, setData] = useState(rowData);
  const [hasError, setHasError] = useState(false);

  useEffect(() => {
    if (!Store.citiesTotalIsGreaterThanTotalMeasured()) {
      setHasError(false);
    }
  }, [apportiongTotal]);

  const handleChangeCity = (name, value) => {
    const newData = Store.setCityOfCityItem(index, value);
    if (newData) setData({ ...newData });
  };

  const handleChangeApportionmentInputs = (event) => {
    const { value, name } = event.target;
    if (decimal(data[name], 2).isEqualTo(value)) return;

    const newData = { ...data };
    newData[name] = value;
    if (newData) setData({ ...newData });
  };

  const handleKeyDownApportionmentValue = (ev) => {
    ev.stopPropagation();
    const key = ev.key;
    if (key === "Enter") {
      handleBlurApportionmentValue();
    }
  };

  const handleBlurApportionmentValue = () => {
    Store.setCitysApportionmentValue(index, data.apportionmentValue || 0);
    const newData = Store.calculateCitysMeasurementAndReadjustValues(index);
    if (newData) setData({ ...newData });

    Store.calculateCitiesItemsApportioningTotal();
    setHasError(Store.citiesTotalIsGreaterThanTotalMeasured());
  };

  const handleKeyDownApportionmentPercentage = (ev) => {
    ev.stopPropagation();
    const key = ev.key;
    if (key === "Enter") {
      handleBlurApportionmentPercentage();
    }
  };

  const handleBlurApportionmentPercentage = () => {
    Store.setCitysApportionmentPercentage(
      index,
      data.apportionmentPercentage || 0
    );
    const newData = Store.calculateCitysMeasurementAndReadjustValues(index);
    if (newData) setData({ ...newData });

    Store.calculateCitiesItemsApportioningTotal();
    setHasError(Store.citiesTotalIsGreaterThanTotalMeasured());
  };

  const handleClickRemove = () => {
    Store.removeCitiesItem(index);
    Store.calculateCitiesItemsApportioningTotal();
  };

  const filterCitiesList = () => {
    const cities = Store.citiesItems.map((it) => it.city?.id || 0);
    return ContractIntegrationStore.citiesItems.filter(
      (city) => !cities.includes(city.id)
    );
  };

  return (
    <Grid
      container
      item
      className={`${classes.line} mb-2`}
      alignItems="flex-start"
      spacing={4}
    >
      <Grid item xs={12} md={3} lg={4}>
        <AutoCompleteField
          name="city"
          label={lang("fields.county")}
          options={filterCitiesList()}
          fullWidth
          value={data.city}
          onChangeOption={handleChangeCity}
          InputProps={{
            margin: "none",
          }}
        />
      </Grid>
      <Grid
        container
        item
        xs={12}
        md={5}
        lg={4}
        direction="row"
        justifyContent="space-between"
        alignItems="flex-start"
      >
        <MaskedField
          label={lang("fields.value")}
          name="apportionmentValue"
          className={classes.input}
          margin="none"
          prefix={`${lang("currency")} `}
          fixedDecimalScale
          allowNegative={false}
          value={data.apportionmentValue}
          onChange={handleChangeApportionmentInputs}
          onBlur={handleBlurApportionmentValue}
          onKeyDown={handleKeyDownApportionmentValue}
          error={hasError}
          helperText={hasError ? lang("billing.valueErrorMessage") : ""}
        />
        <Typography className={classes.text}>{lang("or")}</Typography>
        <MaskedField
          label={lang("fields.percentage")}
          name="apportionmentPercentage"
          className={classes.input}
          margin="none"
          suffix="%"
          allowNegative={false}
          value={data.apportionmentPercentage}
          onChange={handleChangeApportionmentInputs}
          onBlur={handleBlurApportionmentPercentage}
          onKeyDown={handleKeyDownApportionmentPercentage}
          error={hasError}
          helperText={hasError ? lang("billing.percentageErrorMessage") : ""}
        />
      </Grid>
      {separateReadjust && (
        <Grid
          container
          item
          xs={12}
          md={3}
          lg={3}
          direction="row"
          justifyContent="space-evenly"
          alignItems="center"
        >
          <Grid item xs={6}>
            <Typography className={classes.labelValue}>
              {formatNumberToMonetary(data.measurementValue)}
            </Typography>
          </Grid>
          <Grid item xs={6}>
            <Typography className={classes.labelValue}>
              {formatNumberToMonetary(data.readjustmentValue)}
            </Typography>
          </Grid>
        </Grid>
      )}
      <Grid container item xs={12} md={1} lg={1} alignItems="center">
        <IconButton className="actionButton" onClick={handleClickRemove}>
          <CloseIcon />
        </IconButton>
      </Grid>
    </Grid>
  );
};

export default ResourcesByCityRow;
